import React from 'react';
import { Link } from 'react-router-dom';
import useSeo from '../hooks/useSeo';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from '../components/ui/accordion';
import {
  BookOpen, Award, Calendar, Clock, Users, FileText, GraduationCap,
  ChevronRight, ArrowLeft, Sparkles, Target, Scale, Layers, Briefcase, Shield,
} from 'lucide-react';
import {
  REFERENTIEL_META, BLOC_COMPETENCES, MODALITES_EVALUATION,
  DUREE_TOTALE_EPREUVE, VOIES_ACCES, COMPETENCES_TRANSVERSALES, SEPT_ASPECTS,
} from '../data/referentielCCS';
import AudienceContextBanner from '../components/AudienceContextBanner';

export default function ReferentielCCS() {
  useSeo({
    title: 'Référentiel officiel CCS ECSR 2 Roues — Trame complète JO 28/04/2016',
    description: 'Référentiel officiel du CCS ECSR « Animer des actions de formation à la conduite en sécurité des véhicules motorisés à 2 roues » (TP-01303, JO 28/04/2016). Compétences, modalités d\'évaluation (MSP, ET, EF), durée 3h05.',
    path: '/enseignant/referentiel',
    noindex: true,
  });

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="referentiel-ccs-page">
      {/* En-tête */}
      <header className="border-b border-border bg-gradient-to-br from-primary/5 via-background to-background py-8 px-4 md:px-6">
        <div className="max-w-7xl mx-auto space-y-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Link to="/enseignant/dashboard" className="hover:text-primary inline-flex items-center gap-1" data-testid="back-to-dashboard">
              <ArrowLeft className="w-3 h-3" />
              Espace enseignant
            </Link>
            <ChevronRight className="w-3 h-3" />
            <span>Référentiel officiel CCS ECSR</span>
          </div>

          <div className="space-y-2">
            <Badge variant="secondary" className="text-[10px] uppercase tracking-wider" data-testid="badge-code-tp">
              TP-01303 — Niveau 5 (Bac+2) — Ministère du Travail
            </Badge>
            <h1 className="text-2xl md:text-4xl font-bold leading-tight flex items-center gap-3">
              <Award className="w-8 h-8 text-primary flex-shrink-0" />
              Référentiel officiel CCS ECSR 2 Roues
            </h1>
            <p className="text-sm md:text-base text-muted-foreground max-w-3xl">
              <span className="font-semibold text-foreground">{REFERENTIEL_META.intitule}</span>
              {' — '}
              Trame officielle structurée selon l'arrêté du 6 avril 2016 (JO 28/04/2016) modifié par l'arrêté du 25 juin 2018.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <Stat icon={Calendar} label="Publication JO" value={REFERENTIEL_META.dateJO} />
            <Stat icon={Award} label="Code TP" value={REFERENTIEL_META.codeTP} />
            <Stat icon={Layers} label="Niveau" value={REFERENTIEL_META.niveau} />
            <Stat icon={Clock} label="Durée épreuves" value={DUREE_TOTALE_EPREUVE} />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 mt-6 space-y-6">
        {/* Bandeau de contextualisation par audience */}
        <AudienceContextBanner
          pageHint="Le référentiel officiel CCS ECSR sert à 2 publics : tu structures ton enseignement ou tu prépares ton examen ?"
          contexts={{
            enseignant: {
              title: 'Aligne ton enseignement sur le référentiel officiel',
              bullets: [
                'Utilise la trame officielle pour structurer tes cours en salle / sur plateau / en circulation.',
                'Vérifie la conformité de tes fiches séance avec la matrice GDE et les 7 aspects.',
                'Repère les modalités d\'évaluation (MSP, ET, EF) pour cadrer tes simulations.',
              ],
              cta: 'Voir les fondements pédagogiques',
              ctaTo: '/enseignant/fondements-pedagogie',
            },
            'futur-enseignant': {
              title: 'Maîtriser la trame officielle attendue par le jury',
              bullets: [
                'Comprends les blocs de compétences évalués et leur ordre logique.',
                'Mémorise les 7 aspects (Cog, Atom, Soc, Comm, Tech, Émot, Réflex) — citation jury fréquente.',
                'Connais les durées et modalités des 3 épreuves : MSP, ET, EF.',
              ],
              cta: 'Voir les fiches Jury Expert',
              ctaTo: '/enseignant/ccs-jury',
            },
          }}
          showOnEleve={false}
        />

        {/* Cadre réglementaire */}
        <Card data-testid="cadre-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="w-4 h-4 text-primary" />
              Cadre réglementaire
            </CardTitle>
            <CardDescription>Textes officiels et organisme certificateur.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <KV label="Arrêté" value={REFERENTIEL_META.arrete} />
            <KV label="Organisme certificateur" value={REFERENTIEL_META.organismeCertif} />
            <KV label="Code NSF" value={REFERENTIEL_META.codeNSF} />
            <KV label="Durée de formation" value={REFERENTIEL_META.duréeFormation} />
            <KV label="Validité" value={REFERENTIEL_META.validite} />
            <div className="border-t border-border pt-3">
              <div className="font-semibold mb-2">Pré-requis administratifs :</div>
              <ul className="space-y-1">
                {REFERENTIEL_META.prerequisAdministratif.map((p, i) => (
                  <li key={i} className="flex gap-2"><ChevronRight className="w-3 h-3 mt-1 text-primary flex-shrink-0" />{p}</li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Bloc de compétences */}
        <Card data-testid="bloc-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Target className="w-4 h-4 text-primary" />
              Bloc de compétences n°{BLOC_COMPETENCES.numero}
            </CardTitle>
            <CardDescription className="text-foreground font-medium">
              {BLOC_COMPETENCES.intitule}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm leading-relaxed">{BLOC_COMPETENCES.description}</p>

            <Accordion type="multiple" className="space-y-3" data-testid="competences-accordion">
              {BLOC_COMPETENCES.competences.map((c) => (
                <AccordionItem
                  key={c.numero}
                  value={`comp-${c.numero}`}
                  className="border border-border rounded-lg overflow-hidden"
                  data-testid={`competence-${c.numero}`}
                >
                  <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/30">
                    <div className="flex flex-1 items-start gap-3 text-left">
                      <div className="flex-shrink-0 w-10 h-10 rounded-md bg-primary/10 text-primary font-bold text-sm flex items-center justify-center">
                        C{c.numero}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold leading-snug">{c.intitule}</div>
                        <div className="flex flex-wrap items-center gap-2 mt-1.5 text-[10px]">
                          <Badge variant="default" className="gap-1">{c.modaliteEval}</Badge>
                          <Badge variant="outline" className="gap-1"><Clock className="w-3 h-3" />{c.duree}</Badge>
                        </div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pt-2 pb-4 space-y-4">
                    <p className="text-sm leading-relaxed">{c.description}</p>

                    <div className="grid md:grid-cols-2 gap-3">
                      <SubBlock title="Savoirs (théorie)" icon={BookOpen} items={c.savoirs} />
                      <SubBlock title="Savoir-faire (pratique)" icon={Briefcase} items={c.savoirFaire} />
                    </div>

                    <SubBlock title="Critères d'évaluation (ce que le jury observe)" icon={Scale} items={c.criteres} highlight />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>

        {/* Modalités d'évaluation */}
        <Card data-testid="modalites-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Scale className="w-4 h-4 text-primary" />
              Modalités d'évaluation
            </CardTitle>
            <CardDescription>
              Le candidat est évalué sur 3 épreuves successives — durée totale {DUREE_TOTALE_EPREUVE}.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Accordion type="multiple" defaultValue={['MSP', 'ET', 'EF']} className="space-y-3">
              {MODALITES_EVALUATION.map((m) => (
                <AccordionItem
                  key={m.code}
                  value={m.code}
                  className="border border-border rounded-lg overflow-hidden"
                  data-testid={`modalite-${m.code}`}
                >
                  <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/30">
                    <div className="flex flex-1 items-start gap-3 text-left">
                      <Badge variant="default" className="font-mono text-sm px-2.5 py-0.5">{m.code}</Badge>
                      <div className="flex-1">
                        <div className="text-sm font-semibold">{m.intitule}</div>
                        <div className="flex flex-wrap items-center gap-2 mt-1 text-[10px]">
                          <Badge variant="outline"><Clock className="w-3 h-3 mr-1" />{m.duree}</Badge>
                          <Badge variant="secondary">Coefficient {m.coefficient}</Badge>
                          <span className="text-muted-foreground">Compétences {m.competencesEvaluees.join(', ')}</span>
                        </div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pt-2 pb-4 space-y-3">
                    <p className="text-sm leading-relaxed">{m.description}</p>
                    <div className="space-y-2">
                      <div className="text-xs font-semibold uppercase text-muted-foreground">Composition de l'épreuve</div>
                      {m.composition.map((p, i) => (
                        <div key={i} className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs">
                          <div className="flex justify-between items-center mb-0.5">
                            <span className="font-semibold">{p.phase}</span>
                            <Badge variant="outline" className="text-[10px]">{p.duree}</Badge>
                          </div>
                          <p className="text-muted-foreground">{p.detail}</p>
                        </div>
                      ))}
                    </div>
                    <div>
                      <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">Documents attendus</div>
                      <ul className="text-xs space-y-0.5">
                        {m.documentsAttendus.map((d, i) => (
                          <li key={i} className="flex gap-2"><ChevronRight className="w-3 h-3 mt-0.5 text-primary" />{d}</li>
                        ))}
                      </ul>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>

        {/* 7 aspects pédagogiques */}
        <Card data-testid="aspects-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Sparkles className="w-4 h-4 text-primary" />
              Les 7 aspects pédagogiques mobilisables (référentiel d'études de cas)
            </CardTitle>
            <CardDescription>
              Tous les sujets d'études de cas sont rattachés à l'un de ces 7 aspects.
              Le candidat doit savoir les identifier et adapter son intervention.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {SEPT_ASPECTS.map((a) => (
                <div key={a.code} className="rounded-md border border-border bg-muted/20 p-3 space-y-1" data-testid={`aspect-${a.code}`}>
                  <div className="text-xs font-bold uppercase tracking-wider text-primary">{a.intitule}</div>
                  <p className="text-[11px] leading-relaxed text-muted-foreground">{a.description}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 text-center">
              <Button asChild size="sm" variant="default" data-testid="cta-etudes">
                <Link to="/enseignant/etudes-de-cas">
                  👉 Voir les 27 études de cas officielles
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Voies d'accès */}
        <Card data-testid="voies-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="w-4 h-4 text-primary" />
              Voies d'accès à la certification
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {VOIES_ACCES.map((v) => (
              <div key={v.code} className="rounded-md border border-border bg-muted/30 p-3 space-y-1.5" data-testid={`voie-${v.code}`}>
                <div className="text-sm font-semibold">{v.intitule}</div>
                <p className="text-xs text-muted-foreground leading-relaxed">{v.description}</p>
                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  <Info label="Durée" value={v.duree} />
                  <Info label="Coût" value={v.cout} />
                </div>
                <div className="text-[10px] text-muted-foreground">
                  <span className="font-semibold uppercase">Financement : </span>{v.financement.join(' • ')}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Compétences transversales */}
        <Card data-testid="transversales-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Shield className="w-4 h-4 text-primary" />
              Compétences transversales (déontologie professionnelle)
            </CardTitle>
            <CardDescription>
              Évaluées implicitement durant toutes les épreuves, et explicitement durant l'Entretien Final.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {COMPETENCES_TRANSVERSALES.map((c, i) => (
              <div key={i} className="rounded-md border border-border bg-muted/20 p-3 space-y-1.5">
                <div className="text-sm font-semibold text-primary">{c.intitule}</div>
                <p className="text-xs leading-relaxed">{c.description}</p>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {c.indicateurs.map((ind, j) => (
                    <Badge key={j} variant="outline" className="text-[10px]">{ind}</Badge>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* CTA */}
        <Card className="bg-gradient-to-br from-primary/5 to-background border-primary/20">
          <CardContent className="py-6 text-center space-y-3">
            <h2 className="text-lg font-semibold">Prêt à passer le CCS ECSR ?</h2>
            <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
              Préparez chaque épreuve avec nos ressources interactives.
            </p>
            <div className="flex flex-wrap justify-center gap-2 pt-2">
              <Button asChild size="sm" variant="default" data-testid="cta-etudes-2">
                <Link to="/enseignant/etudes-de-cas">📚 27 études de cas</Link>
              </Button>
              <Button asChild size="sm" variant="outline" data-testid="cta-guide-ref">
                <Link to="/enseignant/guide">🎓 Guide ECSR</Link>
              </Button>
              <Button asChild size="sm" variant="outline" data-testid="cta-simulateur-ref">
                <Link to="/enseignant/simulateur-ccs">🎲 Simulateur CCS · Jury IA</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

// ---- Sous-composants ----
function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-md border border-border bg-card px-3 py-2 flex items-center gap-2">
      <Icon className="w-4 h-4 text-primary flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] text-muted-foreground uppercase truncate">{label}</div>
        <div className="text-xs font-semibold truncate">{value}</div>
      </div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3 text-xs">
      <span className="text-muted-foreground uppercase font-semibold min-w-[160px]">{label}</span>
      <span className="text-foreground">{value}</span>
    </div>
  );
}

function SubBlock({ title, icon: Icon, items, highlight = false }) {
  return (
    <div className={`rounded-md p-3 ${highlight ? 'bg-primary/5 border border-primary/20' : 'bg-muted/30 border border-border/50'} space-y-1.5`}>
      <h4 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-foreground/80">
        <Icon className="w-3.5 h-3.5 text-primary" />
        {title}
      </h4>
      <ul className="space-y-1 text-xs">
        {items.map((it, i) => (
          <li key={i} className="flex gap-2"><ChevronRight className="w-3 h-3 mt-0.5 text-primary flex-shrink-0" />{it}</li>
        ))}
      </ul>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="rounded border border-border/50 bg-background px-2 py-1.5">
      <div className="text-[9px] text-muted-foreground uppercase">{label}</div>
      <div className="text-[11px] font-medium leading-snug">{value}</div>
    </div>
  );
}
